import { Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SidebarService {
  private readonly STORAGE_KEY = 'gt-sidebar-collapsed';
  private isCollapsed$ = new BehaviorSubject<boolean>(this.getStoredState());

  // Signal API - synchronized with BehaviorSubject for progressive migration
  private _isCollapsedSignal: WritableSignal<boolean> = signal(this.getStoredState());

  constructor() {
    // Keep Signal in sync with BehaviorSubject
    this.isCollapsed$.subscribe(value => this._isCollapsedSignal.set(value));
  }

  /**
   * Get the current collapsed state as observable (RxJS - legacy API)
   * @deprecated Use isCollapsedSignal for new code
   */
  get isCollapsed(): Observable<boolean> {
    return this.isCollapsed$.asObservable();
  }

  /**
   * Get the current collapsed state as Signal (Angular 16+)
   * Recommended for new code - no need to unsubscribe!
   */
  get isCollapsedSignal() {
    return this._isCollapsedSignal.asReadonly();
  }

  /**
   * Get the current collapsed state as value
   */
  get isCollapsedValue(): boolean {
    return this.isCollapsed$.value;
  }

  /**
   * Toggle the sidebar collapsed state
   */
  toggle(): void {
    const newState = !this.isCollapsed$.value;
    this.setState(newState);
  }

  /**
   * Collapse the sidebar
   */
  collapse(): void {
    this.setState(true);
  }

  /**
   * Expand the sidebar
   */
  expand(): void {
    this.setState(false);
  }

  /**
   * Set the sidebar state
   */
  private setState(collapsed: boolean): void {
    this.isCollapsed$.next(collapsed);
    this.saveState(collapsed);
  }

  /**
   * Get stored state from localStorage
   */
  private getStoredState(): boolean {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : false; // Default to expanded
    } catch (error) {
      console.error('Failed to parse sidebar collapsed state from localStorage:', error);
      return false;
    }
  }

  /**
   * Save state to localStorage
   */
  private saveState(collapsed: boolean): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(collapsed));
    } catch {
      // Handle localStorage errors silently
    }
  }
}